import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import Home from "./components/HomePage/Home";
import Header from "./components/Header";
import Footer from "./components/Footer";
import About from "./components/extras/About";
import Contact from "./components/extras/Contact";
import FAQ from "./components/extras/FAQ";
import LoginPage from "./components/Authentication/Login";
import ResetPasswordPage from "./components/Authentication/ResetPassword";
import JobsPage from "./components/jobs/JobsPage";
import { getUserDetails, pingServer } from "./apis/UserApis";
import JobManagementPage from "./components/admin/AdminPage";
import JobDetailsPage from "./components/jobs/JobDetails";
import AddNewJob from "./components/admin/NewJob";
import CompanyJobsPage from "./components/jobs/CompanyJobsPage";
import { useAuth } from "./components/Authentication/AuthContext";
import SignUpPage from "./components/Authentication/Signup";
import ProfilePage from "./components/Profile";
import UserDashboard from "./components/UserDashboard";
import ScrollToTop from "./components/ScrollToTop";
import CreatePurchase from "./components/admin/CreatePurchase";
import AuthPromptModal from "./components/Authentication/AuthPromptModal";
import JobSubscriptionBanner from "./components/jobs/JobSubscriptionBanner";

// Course related imports
import CoursesPage from "./components/courses/CoursesPage";
import AddCourse from "./components/courses/AddCourse";
import EditCourse from "./components/courses/EditCourse";
import CourseDetails from "./components/courses/CourseDetails";
import AddChapter from "./components/courses/chapters/AddChapter";
import EditChapter from "./components/courses/chapters/EditChapter";
import TopicList from "./components/courses/topics/TopicList";
import AddTopic from "./components/courses/topics/AddTopic";
import EditTopic from "./components/courses/topics/EditTopic";
import TopicView from "./components/courses/topics/TopicView";
import RequireAllowedCourseUser from "./components/Authentication/RequireAllowedCourseUser";
import BootcampPromoModal from "./components/ads/BootcampPromoModal";
import { TopicCacheProvider } from "./components/courses/topics/TopicCacheContext";

function PrivateRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <AuthPromptModal show />;
}

function AdminRoute({ children }) {
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loggedInUser, setLoggedInUser] = useState(null);

  useEffect(() => {
    if (isAuthenticated && user?.userId) {
      getUserDetails(user.userId, user.token)
        .then((data) => {
          setLoggedInUser(data);
        })
        .catch((error) => {
          console.error("Error fetching user details:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center space-y-6">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent border-solid rounded-full animate-spin"></div>
        <p className="text-gray-500 text-center text-sm">
          Checking admin permissions...
        </p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return loggedInUser?.role === "admin" ? children : <Navigate to="/" />;
}

function App() {
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    pingServer();
    const intervalId = setInterval(pingServer, 600000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Header />
        {isAuthenticated && <JobSubscriptionBanner />}
        {isAuthenticated && <BootcampPromoModal />}
        <TopicCacheProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Public home page */}
          <Route path="/" element={<Home />} />

          {/* Everything else requires login */}

          <Route
            path="/about"
            element={
              <PrivateRoute>
                <About />
              </PrivateRoute>
            }
          />
          <Route
            path="/contact"
            element={
              <PrivateRoute>
                <Contact />
              </PrivateRoute>
            }
          />
          <Route
            path="/faq"
            element={
              <PrivateRoute>
                <FAQ />
              </PrivateRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <UserDashboard />
              </PrivateRoute>
            }
          />

          <Route
            path="/jobs"
            element={
              <PrivateRoute>
                <JobsPage key="job" type="job" />
              </PrivateRoute>
            }
          />
          <Route
            path="/internships"
            element={
              <PrivateRoute>
                <JobsPage key="internship" type="internship" />
              </PrivateRoute>
            }
          />
          <Route
            path="/referrals"
            element={
              <PrivateRoute>
                <JobsPage key="referral" type="referral" />
              </PrivateRoute>
            }
          />
          <Route
            path="/jobs/:id"
            element={
              <PrivateRoute>
                <JobDetailsPage user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/jobs/company/:company"
            element={
              <PrivateRoute>
                <CompanyJobsPage />
              </PrivateRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin/add/new"
            element={
              <PrivateRoute>
                <AdminRoute>
                  <AddNewJob />
                </AdminRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <AdminRoute>
                  <JobManagementPage />
                </AdminRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/purchases/new"
            element={
              <PrivateRoute>
                <AdminRoute>
                  <CreatePurchase />
                </AdminRoute>
              </PrivateRoute>
            }
          />

          {/* Course routes */}
          <Route
            path="/courses"
            element={
              <PrivateRoute>
                <RequireAllowedCourseUser>
                  <CoursesPage />
                </RequireAllowedCourseUser>
              </PrivateRoute>
            }
          />
          <Route
            path="/courses/new"
            element={
              <PrivateRoute>
                <AdminRoute>
                  <AddCourse />
                </AdminRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/courses/edit/:courseId"
            element={
              <PrivateRoute>
                <AdminRoute>
                  <EditCourse />
                </AdminRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/courses/:courseId"
            element={
              <PrivateRoute>
                <RequireAllowedCourseUser>
                  <CourseDetails />
                </RequireAllowedCourseUser>
              </PrivateRoute>
            }
          />

          {/* Chapter routes */}
          <Route
            path="/courses/:courseId/chapters/new"
            element={
              <PrivateRoute>
                <AdminRoute>
                  <AddChapter />
                </AdminRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/chapters/edit/:chapterId"
            element={
              <PrivateRoute>
                <AdminRoute>
                  <EditChapter />
                </AdminRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/chapters/:chapterId/topics"
            element={
              <PrivateRoute>
                <RequireAllowedCourseUser>
                  <TopicList />
                </RequireAllowedCourseUser>
              </PrivateRoute>
            }
          />
          <Route
            path="/chapter/:chapterId"
            element={
              <PrivateRoute>
                <RequireAllowedCourseUser>
                  <TopicList />
                </RequireAllowedCourseUser>
              </PrivateRoute>
            }
          />

          {/* Topic routes */}
          <Route
            path="/topics/new"
            element={
              <PrivateRoute>
                <AdminRoute>
                  <AddTopic />
                </AdminRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/topics/edit/:topicId"
            element={
              <PrivateRoute>
                <AdminRoute>
                  <EditTopic />
                </AdminRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/topics/:topicId"
            element={
              <PrivateRoute>
                <RequireAllowedCourseUser>
                  <TopicView />
                </RequireAllowedCourseUser>
              </PrivateRoute>
            }
          />
          <Route
            path="/topic/:topicId"
            element={
              <PrivateRoute>
                <RequireAllowedCourseUser>
                  <TopicView />
                </RequireAllowedCourseUser>
              </PrivateRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </TopicCacheProvider>

        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
